<template>
    <TransitionRoot as="template" :show="open">
        <Dialog class="relative z-50" @close="backdropClose" :static="static || showPrintModal">
            <TransitionChild
                as="template"
                enter="ease-in-out duration-500"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-500"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-hidden">
                <div class="absolute inset-0 overflow-hidden">
                    <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-2 sm:pl-10">
                        <TransitionChild
                            as="template"
                            enter="transform transition ease-in-out duration-500 sm:duration-700"
                            enter-from="translate-x-full"
                            enter-to="translate-x-0"
                            leave="transform transition ease-in-out duration-500 sm:duration-700"
                            leave-from="translate-x-0"
                            leave-to="translate-x-full"
                            @after-enter="standardCalculator?.focus()"
                        >
                            <DialogPanel
                                class="pointer-events-auto relative flex h-full w-screen max-w-2xl flex-col bg-slate-50 shadow-2xl shadow-slate-900/10"
                            >
                                <!-- Header -->
                                <div class="relative overflow-hidden bg-indigo-600 px-5 py-4 text-white">
                                    <div class="pointer absolute -top-10 -right-8 size-36 rounded-full bg-white/10" />
                                    <div class="pointer absolute right-16 -bottom-12 size-28 rounded-full bg-indigo-500/40" />
                                    <div class="relative flex items-start justify-between gap-3">
                                        <div class="min-w-0">
                                            <div class="flex items-center gap-2.5">
                                                <span
                                                    class="inline-flex size-9 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20"
                                                >
                                                    <CalculatorIcon class="size-5" aria-hidden="true" />
                                                </span>
                                                <div class="min-w-0">
                                                    <DialogTitle class="truncate text-base font-semibold tracking-tight">
                                                        {{ isInteriorMode ? 'Interior Calculator' : 'Calculator' }}
                                                    </DialogTitle>
                                                    <p class="mt-0.5 truncate text-xs text-indigo-100">
                                                        <template v-if="!isInteriorMode">Quick sums, with every one kept in memory</template>
                                                        <template v-else-if="currentRoom">{{ currentRoom.name }} · Room estimate</template>
                                                        <template v-else>Wall panels, ceiling & flooring quantities</template>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            class="relative rounded-lg p-1.5 text-indigo-100 transition hover:bg-white/10 hover:text-white focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white"
                                            @click="closeModal"
                                        >
                                            <span class="absolute -inset-2.5" />
                                            <span class="sr-only">Close panel</span>
                                            <XMarkIcon class="size-5" aria-hidden="true" />
                                        </button>
                                    </div>

                                    <div v-if="interiorEnabled" class="relative mt-3 inline-flex rounded-xl bg-white/10 p-1 ring-1 ring-white/20">
                                        <button
                                            v-for="option in modeOptions"
                                            :key="option.value"
                                            type="button"
                                            @click="setMode(option.value)"
                                            :class="[
                                                mode === option.value
                                                    ? 'bg-white text-indigo-700 shadow-sm'
                                                    : 'text-indigo-100 hover:bg-white/10 hover:text-white',
                                                'cursor-pointer rounded-lg px-3 py-1.5 text-xs font-semibold transition',
                                            ]"
                                        >
                                            {{ option.label }}
                                        </button>
                                    </div>
                                </div>

                                <StandardCalculator v-if="!isInteriorMode" ref="standardCalculator" />

                                <!-- Main view: add room -->
                                <div v-if="isInteriorMode && view === 'main'" class="border-b border-slate-200/80 bg-white px-5 py-4">
                                    <label for="roomName" class="mb-1.5 block text-xs font-medium tracking-wide text-slate-500 uppercase">
                                        New room
                                    </label>
                                    <div class="flex w-full flex-wrap items-center gap-2 sm:flex-nowrap">
                                        <input
                                            type="text"
                                            name="roomName"
                                            id="roomName"
                                            v-model="roomName"
                                            placeholder="e.g. Living Room, Master Bedroom"
                                            @keydown.enter.prevent="addNewRoom"
                                            class="block min-w-0 flex-1 rounded-xl border-0 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-900 outline-1 -outline-offset-1 outline-slate-200 placeholder:text-slate-400 focus:bg-white focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 dark:text-white"
                                        />
                                        <button
                                            type="button"
                                            @click="addNewRoom"
                                            class="inline-flex w-full shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:w-auto"
                                        >
                                            <PlusIcon class="size-4" aria-hidden="true" />
                                            Add Room
                                        </button>
                                    </div>
                                </div>

                                <!-- Main view: room list -->
                                <div v-if="isInteriorMode && view === 'main'" class="min-h-0 flex-1 overflow-y-auto">
                                    <div class="space-y-3 p-5">
                                        <template v-if="calcStore.getRooms.length > 0">
                                            <div
                                                v-for="room in calcStore.getRooms"
                                                :key="room.id"
                                                class="group flex w-full items-center gap-2 rounded-2xl border border-slate-200/80 bg-white p-2 pr-2 shadow-sm transition hover:border-indigo-200 hover:shadow-md"
                                            >
                                                <button
                                                    type="button"
                                                    class="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-xl p-2 text-left transition hover:bg-slate-50"
                                                    @click="setCurrentRoom(room.id)"
                                                >
                                                    <span
                                                        class="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-sm font-semibold text-indigo-700 ring-1 ring-indigo-100"
                                                    >
                                                        {{ roomInitial(room.name) }}
                                                    </span>
                                                    <div class="min-w-0 flex-1">
                                                        <p
                                                            class="truncate text-sm font-semibold text-slate-900 group-hover:text-indigo-700 dark:text-white"
                                                        >
                                                            {{ room.name }}
                                                        </p>
                                                        <div class="mt-1.5 flex flex-wrap gap-1.5">
                                                            <span
                                                                class="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
                                                            >
                                                                {{ room.panels.panelCount || 0 }} panels
                                                            </span>
                                                            <span
                                                                class="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
                                                            >
                                                                {{ room.ceiling.ceilingCount || 0 }} ceiling
                                                            </span>
                                                            <span
                                                                class="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
                                                            >
                                                                {{ room.flooring?.flooringCount || 0 }} flooring
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <ChevronRightIcon
                                                        class="size-5 shrink-0 text-slate-300 transition group-hover:text-indigo-500"
                                                        aria-hidden="true"
                                                    />
                                                </button>
                                                <button
                                                    type="button"
                                                    @click="removeRoom(room.id)"
                                                    class="shrink-0 cursor-pointer rounded-lg p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                                                    :title="`Remove ${room.name}`"
                                                >
                                                    <TrashIcon class="size-4" aria-hidden="true" />
                                                    <span class="sr-only">Remove {{ room.name }}</span>
                                                </button>
                                            </div>
                                        </template>
                                        <div
                                            v-else
                                            class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-14 text-center"
                                        >
                                            <span
                                                class="mb-3 inline-flex size-12 items-center justify-center rounded-2xl bg-slate-50 text-slate-400 ring-1 ring-slate-100"
                                            >
                                                <HomeIcon class="size-6" aria-hidden="true" />
                                            </span>
                                            <p class="text-sm font-medium text-slate-800">No rooms yet</p>
                                            <p class="mt-1 max-w-xs text-xs leading-relaxed text-slate-500">
                                                Add a room above, then open it to select products and enter wall, ceiling, and floor measurements.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Main view: summary footer -->
                                <div v-if="isInteriorMode && view === 'main'" class="border-t border-slate-200/80 bg-white p-5">
                                    <div
                                        class="rounded-2xl bg-gradient-to-br from-indigo-50 to-slate-50 p-4 ring-1 ring-indigo-100/80 dark:from-indigo-950/50 dark:to-slate-900/80 dark:ring-indigo-500/25"
                                    >
                                        <div class="flex items-center justify-between gap-2">
                                            <h4 class="text-xs font-semibold tracking-wide text-indigo-500/80 uppercase dark:text-indigo-300">
                                                Project totals
                                            </h4>
                                            <span class="text-[11px] text-slate-500">Includes 1% waste</span>
                                        </div>
                                        <div class="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                                            <div
                                                class="rounded-xl bg-white/70 px-3 py-2.5 ring-1 ring-slate-200/80 dark:bg-slate-900/70 dark:ring-white/10"
                                            >
                                                <p class="text-[11px] text-slate-500">Rooms</p>
                                                <p class="mt-0.5 text-lg font-semibold text-slate-900 tabular-nums dark:text-white">
                                                    {{ calcStore.getRooms.length }}
                                                </p>
                                            </div>
                                            <div
                                                class="rounded-xl bg-white/70 px-3 py-2.5 ring-1 ring-slate-200/80 dark:bg-slate-900/70 dark:ring-white/10"
                                            >
                                                <p class="text-[11px] text-slate-500">Panels</p>
                                                <p class="mt-0.5 text-lg font-semibold text-slate-900 tabular-nums dark:text-white">
                                                    {{ calcStore.totalPanels }}
                                                </p>
                                            </div>
                                            <div
                                                class="rounded-xl bg-white/70 px-3 py-2.5 ring-1 ring-slate-200/80 dark:bg-slate-900/70 dark:ring-white/10"
                                            >
                                                <p class="text-[11px] text-slate-500">Ceiling</p>
                                                <p class="mt-0.5 text-lg font-semibold text-slate-900 tabular-nums dark:text-white">
                                                    {{ calcStore.totalCeilings }}
                                                </p>
                                            </div>
                                            <div
                                                class="rounded-xl bg-white/70 px-3 py-2.5 ring-1 ring-slate-200/80 dark:bg-slate-900/70 dark:ring-white/10"
                                            >
                                                <p class="text-[11px] text-slate-500">Flooring</p>
                                                <p class="mt-0.5 text-lg font-semibold text-slate-900 tabular-nums dark:text-white">
                                                    {{ calcStore.totalFloorings }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-3 flex w-full flex-wrap gap-2">
                                        <button
                                            @click="openPrintModal"
                                            type="button"
                                            :disabled="!calcStore.getRooms.length"
                                            class="inline-flex min-w-[10rem] flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <PrinterIcon class="size-4" aria-hidden="true" />
                                            Print report
                                        </button>
                                        <button
                                            type="button"
                                            @click="confirmReset"
                                            :disabled="!calcStore.getRooms.length"
                                            class="inline-flex min-w-[10rem] flex-1 cursor-pointer items-center justify-center rounded-xl bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            Reset all
                                        </button>
                                    </div>
                                </div>

                                <!-- Room view: tabs -->
                                <div v-if="isInteriorMode && view === 'room'" class="border-b border-slate-200/80 bg-white px-5 pt-3 pb-0">
                                    <div class="flex items-center justify-between gap-3">
                                        <button
                                            type="button"
                                            @click="backToMainView"
                                            class="inline-flex cursor-pointer items-center gap-1 rounded-lg px-1.5 py-1 text-xs font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                                        >
                                            <ArrowLeftIcon class="size-3.5" aria-hidden="true" />
                                            All rooms
                                        </button>
                                    </div>
                                    <nav class="mt-2 -mb-px flex overflow-x-auto" aria-label="Estimate sections">
                                        <button
                                            v-for="tab in tabs"
                                            :key="tab.name"
                                            type="button"
                                            @click="openTab(tab.name)"
                                            :aria-current="tab.current ? 'page' : undefined"
                                            :class="[
                                                tab.current
                                                    ? 'border-indigo-500 text-indigo-600'
                                                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                                                'flex cursor-pointer items-center gap-2 border-b-2 px-4 py-4 text-sm font-medium whitespace-nowrap',
                                            ]"
                                        >
                                            {{ tab.name }}
                                        </button>
                                    </nav>
                                </div>

                                <!-- Room view: summary + product select -->
                                <div v-if="isInteriorMode && view === 'room' && currentRoom" class="border-b border-slate-200/80 bg-white px-5 py-4">
                                    <div
                                        class="rounded-2xl bg-gradient-to-br from-indigo-50 to-slate-50 p-4 ring-1 ring-indigo-100/80 dark:from-indigo-950/50 dark:to-slate-900/80 dark:ring-indigo-500/25"
                                    >
                                        <div class="flex items-start justify-between gap-3">
                                            <div>
                                                <p class="text-xs font-medium tracking-wide text-indigo-500/80 uppercase dark:text-indigo-300">
                                                    {{ getActiveTab.name }} required
                                                </p>
                                                <p class="mt-1 text-3xl font-semibold tracking-tight text-slate-900 tabular-nums dark:text-white">
                                                    {{ activeRequiredQty }}
                                                </p>
                                            </div>
                                            <div class="text-right text-[11px] leading-relaxed text-slate-500">
                                                <p v-if="activeProductSize">Panel {{ activeProductSize }}</p>
                                                <p v-else>Select a product below</p>
                                                <p class="mt-0.5">+1% waste allowance</p>
                                            </div>
                                        </div>
                                        <p
                                            v-if="panelsBreakdown?.openingsExceedWalls && getActiveTab.name === 'Panels'"
                                            class="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-100"
                                        >
                                            Opening area exceeds wall area — net wall coverage is treated as zero.
                                        </p>
                                        <p v-else-if="getActiveTab.name === 'Panels' && panelsBreakdown" class="mt-3 text-[11px] text-slate-500">
                                            Net wall area
                                            {{ formatSqFt(panelsBreakdown.netArea) }}
                                            <span v-if="panelsBreakdown.openingArea > 0">
                                                (walls {{ formatSqFt(panelsBreakdown.wallArea) }} − openings
                                                {{ formatSqFt(panelsBreakdown.openingArea) }})
                                            </span>
                                        </p>
                                        <p v-else-if="surfaceBreakdown" class="mt-3 text-[11px] text-slate-500">
                                            Covered area {{ formatSqFt(surfaceBreakdown.area) }}
                                        </p>
                                    </div>
                                    <div class="mt-3">
                                        <label class="mb-1.5 block text-xs font-medium text-slate-500">
                                            {{ productSelectLabel }}
                                        </label>
                                        <CustomSelect
                                            v-if="getActiveTab.name === 'Panels'"
                                            ref="panelProductSelect"
                                            id="panelProduct"
                                            placeholder="Search wall panel by name, SKU, or code"
                                            data-source="productVariant/getVariantOptionsForCalculator"
                                            v-model="panelProduct"
                                        />
                                        <CustomSelect
                                            v-else-if="getActiveTab.name === 'Ceiling'"
                                            ref="ceilingProductSelect"
                                            id="ceilingProduct"
                                            placeholder="Search ceiling product by name, SKU, or code"
                                            data-source="productVariant/getVariantOptionsForCalculator"
                                            v-model="ceilingProduct"
                                        />
                                        <CustomSelect
                                            v-else-if="getActiveTab.name === 'Flooring'"
                                            ref="flooringProductSelect"
                                            id="flooringProduct"
                                            placeholder="Search flooring product by name, SKU, or code"
                                            data-source="productVariant/getVariantOptionsForCalculator"
                                            v-model="flooringProduct"
                                        />
                                        <p class="mt-1.5 text-[11px] text-slate-400">Only active products with length & width (inches) are listed.</p>
                                    </div>
                                </div>

                                <!-- Room view: measurements -->
                                <div v-if="isInteriorMode && view === 'room' && currentRoom" class="min-h-0 flex-1 overflow-y-auto">
                                    <div class="space-y-4 p-5">
                                        <p class="text-[11px] leading-relaxed text-slate-500">
                                            Enter sizes as feet (<span class="font-medium text-slate-600">10</span>), feet & inches (<span
                                                class="font-medium text-slate-600"
                                                >10'2"</span
                                            >, or inches only <span class="font-medium text-slate-600">24"</span>.
                                        </p>
                                        <template v-if="getActiveTab.name === 'Panels'">
                                            <Measurements
                                                :active-tab="getActiveTab.name"
                                                :current-room="currentRoom"
                                                type="wall"
                                                :data="currentRoom.panels.walls"
                                            />
                                            <Measurements
                                                :active-tab="getActiveTab.name"
                                                :current-room="currentRoom"
                                                type="window"
                                                :data="currentRoom.panels.windows"
                                            />
                                            <Measurements
                                                :active-tab="getActiveTab.name"
                                                :current-room="currentRoom"
                                                type="door"
                                                :data="currentRoom.panels.doors"
                                            />
                                            <Measurements
                                                :active-tab="getActiveTab.name"
                                                :current-room="currentRoom"
                                                type="vent"
                                                :data="currentRoom.panels.vents"
                                            />
                                        </template>
                                        <template v-else-if="getActiveTab.name === 'Ceiling'">
                                            <Measurements
                                                :active-tab="getActiveTab.name"
                                                :current-room="currentRoom"
                                                type="roof"
                                                label="Ceiling section"
                                                :data="currentRoom.ceiling.roof"
                                            />
                                        </template>
                                        <template v-else-if="getActiveTab.name === 'Flooring'">
                                            <Measurements
                                                :active-tab="getActiveTab.name"
                                                :current-room="currentRoom"
                                                type="floor"
                                                label="Floor section"
                                                :data="currentRoom.flooring.floor"
                                            />
                                        </template>
                                    </div>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
    <PrintModal v-if="showPrintModal" @close-modal="showPrintModal = false" />
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { ArrowLeftIcon, CalculatorIcon, ChevronRightIcon, HomeIcon, PlusIcon, PrinterIcon, TrashIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { useCalculatorStore } from '@/stores/calculator-store.js';
import { useConfigurationStore } from '@/stores/configuration-store.js';
import Measurements from '@/components/Portal/Calculator/Measurements.vue';
import StandardCalculator from '@/components/Portal/Calculator/StandardCalculator.vue';
import CustomSelect from '@/components/Select.vue';
import PrintModal from '@/components/Portal/Calculator/PrintModal.vue';

const props = defineProps({
    static: {
        type: Boolean,
        default: false,
    },
});
const emits = defineEmits(['closeModal']);
const calcStore = useCalculatorStore();
const configStore = useConfigurationStore();

const open = ref(true);
const showPrintModal = ref(false);
const syncingProducts = ref(false);
const standardCalculator = ref(null);

// The plain calculator is always available; the room estimator is the add-on.
const interiorEnabled = computed(() => Boolean(configStore.isAddonEnabled('interior_design_calculator')));
const modeOptions = [
    { value: 'standard', label: 'Calculator' },
    { value: 'interior', label: 'Interior' },
];
const mode = ref(interiorEnabled.value && calcStore.calculatorMode === 'interior' ? 'interior' : 'standard');
const isInteriorMode = computed(() => interiorEnabled.value && mode.value === 'interior');

const setMode = (value) => {
    mode.value = value;
    calcStore.setCalculatorMode(value);
};

watch(interiorEnabled, (enabled) => {
    if (!enabled) mode.value = 'standard';
});

const tabs = ref([
    { name: 'Panels', current: true },
    { name: 'Ceiling', current: false },
    { name: 'Flooring', current: false },
]);

const openTab = (tabName) => {
    tabs.value.forEach((tab) => {
        tab.current = tab.name === tabName;
    });
};

const getActiveTab = computed(() => tabs.value.find((tab) => tab.current));

const view = ref('main');
const roomName = ref('');
const currentRoom = ref(null);

const panelProductSelect = ref(null);
const panelProduct = ref(null);
const ceilingProductSelect = ref(null);
const ceilingProduct = ref(null);
const flooringProductSelect = ref(null);
const flooringProduct = ref(null);

const emptyProduct = () => ({ id: null, name: '', price: 0, length: 0, width: 0, extra: '' });

const toSelectOption = (product) => {
    if (!product?.id) return null;
    return {
        id: product.id,
        text: product.name,
        value: product.id,
        extra: product.extra,
        name: product.name,
        price: product.price,
        length: product.length,
        width: product.width,
    };
};

const toStoreProduct = (value) => {
    if (!value?.id) return emptyProduct();
    return {
        id: value.id,
        name: value.name,
        price: value.price,
        length: value.length,
        width: value.width,
        extra: value.extra,
    };
};

const roomInitial = (name) => {
    const trimmed = String(name || '').trim();
    return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
};

const formatProductSize = (product) => {
    if (!product?.id) return '';
    const w = Number(product.width) || 0;
    const l = Number(product.length) || 0;
    if (w <= 0 || l <= 0) return '';
    return `${w}" × ${l}"`;
};

const formatSqFt = (sqInches) => {
    const area = Number(sqInches) || 0;
    const sqFt = area / 144;
    if (sqFt <= 0) return '0 sq ft';
    return `${sqFt.toFixed(sqFt >= 10 ? 1 : 2)} sq ft`;
};

const panelsBreakdown = computed(() => {
    const room = currentRoom.value;
    if (!room) return null;
    // Track reactive room fields so the summary recomputes after edits.
    void room.panels.panelCount;
    void room.panels.walls.length;
    void room.panels.windows.length;
    void room.panels.doors.length;
    void room.panels.vents.length;
    void room.panels.product?.id;
    void room.panels.product?.width;
    void room.panels.product?.length;
    return calcStore.getPanelsBreakdown(room.id);
});

const surfaceBreakdown = computed(() => {
    const room = currentRoom.value;
    if (!room) return null;
    if (getActiveTab.value?.name === 'Ceiling') {
        void room.ceiling.ceilingCount;
        void room.ceiling.roof.length;
        void room.ceiling.product?.id;
        void room.ceiling.product?.width;
        void room.ceiling.product?.length;
        return calcStore.getSurfaceBreakdown(room.id, 'ceiling');
    }
    if (getActiveTab.value?.name === 'Flooring') {
        void room.flooring.flooringCount;
        void room.flooring.floor.length;
        void room.flooring.product?.id;
        void room.flooring.product?.width;
        void room.flooring.product?.length;
        return calcStore.getSurfaceBreakdown(room.id, 'flooring');
    }
    return null;
});

const activeRequiredQty = computed(() => {
    if (!currentRoom.value) return '—';
    if (getActiveTab.value?.name === 'Panels') {
        return panelsBreakdown.value?.hasProduct ? panelsBreakdown.value.count : '—';
    }
    if (getActiveTab.value?.name === 'Ceiling') {
        return surfaceBreakdown.value?.hasProduct ? surfaceBreakdown.value.count : '—';
    }
    if (getActiveTab.value?.name === 'Flooring') {
        return surfaceBreakdown.value?.hasProduct ? surfaceBreakdown.value.count : '—';
    }
    return '—';
});

const activeProductSize = computed(() => {
    if (!currentRoom.value) return '';
    if (getActiveTab.value?.name === 'Panels') return formatProductSize(currentRoom.value.panels.product);
    if (getActiveTab.value?.name === 'Ceiling') return formatProductSize(currentRoom.value.ceiling.product);
    if (getActiveTab.value?.name === 'Flooring') return formatProductSize(currentRoom.value.flooring?.product);
    return '';
});

const productSelectLabel = computed(() => {
    if (getActiveTab.value?.name === 'Panels') return 'Wall panel product';
    if (getActiveTab.value?.name === 'Ceiling') return 'Ceiling product';
    return 'Flooring product';
});

const addNewRoom = () => {
    if (roomName.value.trim() !== '') {
        calcStore.addRoom(roomName.value);
        roomName.value = '';
    }
};

const setCurrentRoom = (id) => {
    currentRoom.value = calcStore.getRoomById(id);
    view.value = 'room';
};

const backToMainView = () => {
    currentRoom.value = null;
    view.value = 'main';
};

const removeRoom = (id) => {
    if (currentRoom.value?.id === id) {
        backToMainView();
    }
    calcStore.removeRoom(id);
};

const openPrintModal = () => {
    if (!calcStore.getRooms.length) return;
    showPrintModal.value = true;
};

const confirmReset = () => {
    if (!calcStore.getRooms.length) return;
    if (window.confirm('Reset all rooms and calculations? This cannot be undone.')) {
        calcStore.resetCalculator();
        backToMainView();
    }
};

watch(panelProduct, (newValue) => {
    if (syncingProducts.value || !currentRoom.value) return;
    calcStore.updateProduct(currentRoom.value.id, 'panels', toStoreProduct(newValue));
});

watch(ceilingProduct, (newValue) => {
    if (syncingProducts.value || !currentRoom.value) return;
    calcStore.updateProduct(currentRoom.value.id, 'ceiling', toStoreProduct(newValue));
});

watch(flooringProduct, (newValue) => {
    if (syncingProducts.value || !currentRoom.value) return;
    calcStore.updateProduct(currentRoom.value.id, 'flooring', toStoreProduct(newValue));
});

watch(
    () => currentRoom.value?.id,
    async (newId) => {
        const room = currentRoom.value;
        if (!room || !newId) {
            panelProduct.value = null;
            ceilingProduct.value = null;
            flooringProduct.value = null;
            return;
        }

        syncingProducts.value = true;
        panelProduct.value = toSelectOption(room.panels.product);
        ceilingProduct.value = toSelectOption(room.ceiling.product);
        flooringProduct.value = toSelectOption(room.flooring?.product);
        calcStore.recalculateRoom(room.id);
        await nextTick();
        syncingProducts.value = false;
    },
    { immediate: true },
);

const closeModal = () => {
    open.value = false;
    setTimeout(() => {
        emits('closeModal');
    }, 500);
};

const backdropClose = () => {
    if (showPrintModal.value) {
        return;
    }
    if (!props.static) {
        closeModal();
    }
};
</script>
